import { LayoutDashboard, Users, FileText, Settings, Plus, Play, Building2, LogOut, Receipt, FileSignature, Kanban, Package } from 'lucide-react';
import { NavLink } from '@/shared/components/NavLink';
import { useNavigate } from 'react-router-dom';
import { useLocation } from 'react-router-dom';
import { useLanguage } from '@/shared/contexts/LanguageContext';
import { useStudio } from '@/features/studio/contexts/StudioContext';
import { useAuth } from '@/features/auth/contexts/AuthContext';
import { toast } from 'sonner';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarFooter,
  SidebarMenuButton,
  SidebarMenuItem,
  useSidebar,
} from '@/components/ui/sidebar';

export function AppSidebar() {
  const navigate = useNavigate();
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';
  const location = useLocation();
  const { t } = useLanguage();
  const { membership } = useStudio();
  const { signOut, isAdmin } = useAuth();

  const isActive = (path: string) => location.pathname === path;

  const group1 = [
    { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
    { path: '/clients', icon: Users, label: t('nav.clients') },
    { path: '/sessions', icon: Play, label: 'Sessions' },
    { path: '/templates', icon: FileText, label: t('nav.templates') },
  ];

  const group2 = [
    { path: '/invoices', icon: Receipt, label: 'Factures' },
    { path: '/quotes', icon: FileSignature, label: 'Devis' },
    { path: '/pipeline', icon: Kanban, label: 'Pipeline' },
    { path: '/inventory', icon: Package, label: 'Inventaire', comingSoon: true },
  ];

  const group3 = [
    ...(membership?.isOwner || isAdmin ? [{ path: '/studio', icon: Building2, label: 'Studio' }] : []),
    { path: '/settings', icon: Settings, label: t('nav.settings') },
  ];

  const renderNavLink = (item: { path: string; icon: React.ElementType; label: string; comingSoon?: boolean }, extraClass = '') => {
    if (item.comingSoon && !isAdmin) {
      return (
        <SidebarMenuItem key={item.path}>
          <SidebarMenuButton asChild>
            <button
              onClick={() => toast.info('Cette fonctionnalité arrive prochainement')}
              className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-sidebar-foreground opacity-50 cursor-not-allowed ${extraClass}`}
            >
              <item.icon size={18} className="shrink-0" />
              {!collapsed && <span className="flex-1 text-left">{item.label}</span>}
              {!collapsed && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-muted text-muted-foreground font-medium shrink-0">
                  Bientôt
                </span>
              )}
            </button>
          </SidebarMenuButton>
        </SidebarMenuItem>
      );
    }
    return (
    <SidebarMenuItem key={item.path}>
      <SidebarMenuButton asChild isActive={isActive(item.path)}>
        <NavLink
          to={item.path}
          end={item.path === '/'}
          className={`flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-sidebar-foreground hover:text-foreground hover:bg-sidebar-accent transition-all duration-200 ${extraClass}`}
          activeClassName="!text-primary !bg-primary/10 font-medium"
        >
          <item.icon size={18} className="shrink-0" />
          {!collapsed && <span>{item.label}</span>}
        </NavLink>
      </SidebarMenuButton>
    </SidebarMenuItem>
    );
  };

  return (
    <Sidebar collapsible="icon" className="glass-sidebar border-r-0">
      <SidebarContent className="pt-4">
        {/* Logo */}
        <div className={`flex items-center gap-3 px-4 py-4 mb-4 ${collapsed ? 'justify-center' : ''}`}>
          {collapsed ? (
            <img src="/symbole.svg" alt="Consentink" className="w-9 h-9 object-contain" />
          ) : (
            <img src="/logo-blanc.svg" alt="Consentink" className="h-8 object-contain" />
          )}
        </div>

        {/* Quick action */}
        {!collapsed && (
          <div className="px-3 mb-4">
            <button
              onClick={() => {
                const event = new CustomEvent('open-new-client-dialog');
                window.dispatchEvent(event);
                navigate('/clients');
              }}
              className="w-full flex items-center gap-2 px-4 py-2.5 bg-primary text-primary-foreground rounded-xl text-sm font-medium hover:opacity-90 transition-opacity"
            >
              <Plus size={16} />
              <span>Nouveau client</span>
            </button>
          </div>
        )}

        {/* Group 1 — Navigation principale */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {group1.map((item) => renderNavLink(item))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="my-2 border-t border-border/40" />

        {/* Group 2 — Navigation secondaire */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {group2.map((item) => renderNavLink(item, 'opacity-80 text-sm'))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        <div className="my-2 border-t border-border/40" />

        {/* Group 3 — Studio + Réglages */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {group3.map((item) => renderNavLink(item))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      <SidebarFooter className="p-3 border-t border-sidebar-border">
        <button
          onClick={() => signOut()}
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm text-sidebar-foreground hover:text-destructive hover:bg-destructive/10 transition-all duration-200"
        >
          <LogOut size={18} className="shrink-0" />
          {!collapsed && <span>Déconnexion</span>}
        </button>
      </SidebarFooter>
    </Sidebar>
  );
}
